import { Box, Tooltip, Typography } from '@mui/material'
import { LockOutlined } from '@mui/icons-material'
import { copyFor } from './bundleCopy'

/**
 * One line saying a paid bundle would answer this, where there is no room for
 * a teaser.
 *
 * `BundleTeaser` is a blurred table and needs a panel to live in. The agent
 * card has a few lines, so the surfaces on it had no way to say anything and
 * rendered NOTHING instead — which is how a tenant who switched Exposure off
 * saw a product that "seems to be working just fine" and could not tell what
 * they had lost.
 *
 * Why silence was the wrong default, having been a deliberate choice
 * -----------------------------------------------------------------
 * The reasoning was sound and incomplete. The rule it protected is real: an
 * agent missing from a within-scan index must not render as "nothing reaches
 * this agent", because `agent_id_hint` embeds the definition's line number and
 * absence has a mechanical cause. So these surfaces return null rather than
 * claiming an absence.
 *
 * But "we could not compute this for your agent" and "you do not hold this
 * bundle" are different sentences with different remedies, and both were being
 * answered by showing nothing. Worse, `AgentBlastRadius` answered the second
 * with the FIRST's explanation — a tooltip about scan recency and line numbers,
 * which is a confident, specific and wrong account of why the number is
 * missing.
 *
 * So: absence stays silent, and REFUSAL says so. Three states, as everywhere
 * else in this product.
 *
 * It claims nothing about the estate
 * ----------------------------------
 * No count, no verdict, no "0". It names the bundle and what the bundle
 * answers. A tenant reading this learns what they do not have, never something
 * untrue about what they do have.
 */
interface BundleLockedProps {
  /** The entitlement feature key the gate refused on. Copy is looked up by the
   *  same key, so a surface cannot describe a capability differently from the
   *  teaser that sells it. */
  feature: string
  /** Shown beside the bundle name. Defaults to the capability's own label, so
   *  a reader learns WHICH thing is missing and not only that something is. */
  label?: string
}

const BundleLocked = ({ feature, label }: BundleLockedProps) => {
  const copy = copyFor(feature)
  if (!copy) return null
  return (
    <Tooltip
      title={`${copy.headline} ${copy.detail} Part of ${copy.bundle}.`}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
        <LockOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.disabled">
          {label ? `${label} — ${copy.bundle}` : copy.bundle}
        </Typography>
      </Box>
    </Tooltip>
  )
}

export default BundleLocked
